/**
 * DIE SUCHE DER PROJEKTVERWALTUNG (components/admin/AdminProjectList.tsx).
 *
 * Bis hierher fand die Suche nur Projektname, Nummer und Adresse — und auch
 * die nur, wenn die Eingabe als GANZES in einem Feld stand. „Wiese" (der
 * Bauleiter) oder „meyer hamburg" fanden nichts, obwohl die Liste die Daten
 * dafuer laengst laedt: `metadata:data->metadata` (queryColumns.ts) bringt
 * Bauherr, Auftraggeber, Ansprechpartner, Verantwortliche und den Verteiler
 * (`distributionList`, die Blob-Kopie von `project_contacts`) mit. Diese
 * Datei durchsucht genau das — ohne eine einzige weitere Abfrage.
 *
 * ─── Regeln ─────────────────────────────────────────────────────────────────
 * - Jedes Wort der Eingabe muss in IRGENDEINEM Feld stehen (UND ueber die
 *   Woerter, ODER ueber die Felder). Damit ist die neue Suche eine Obermenge
 *   der alten: stand die ganze Eingabe in einem Feld, steht dort auch jedes
 *   einzelne Wort.
 * - Gross/klein, Akzente/Umlaute und ß zaehlen nicht; „Straße" findet
 *   „Str.", und „Str." findet „Straße".
 * - Die UUID trifft wie bisher nur VOLLSTAENDIG — Teilstuecke einer UUID
 *   passen auf zu vieles.
 *
 * Gefaltet wird ZEICHENWEISE mit Rueckverweis auf die Originalstelle, damit
 * die Markierung in der Zeile das Original trifft, auch wenn „ß" beim Falten
 * zu zwei Zeichen wird.
 */
import type { Contact, ReportMetadata } from '../types';
import { getRepresentatives } from './projectRepresentatives';

/** Ein Projekt der Liste, soweit die Suche es liest. */
export interface SuchbaresProjekt {
    id?: string | null;
    name?: string | null;
    project_number?: string | null;
    address?: string | null;
    client_name?: string | null;
    data?: { metadata?: Partial<ReportMetadata> | null } | null;
}

/** Ein durchsuchbares Feld eines Projekts. */
export interface Suchfeld {
    /** Wofuer der Wert steht — erscheint vor dem Treffer („Bauherr: …"). */
    feld: string;
    /** Der Originaltext, einzeilig. */
    wert: string;
    /** Steht der Wert ohnehin in der Zeile? Dann braucht er keine eigene Trefferzeile. */
    sichtbar: boolean;
}

interface GefaltetesFeld extends Suchfeld {
    gefaltet: string;
}

/** Vorbereitete Felder eines Projekts — einmal je Projektliste, nicht je Tastendruck. */
export interface ProjektSuchindex {
    id: string;
    felder: GefaltetesFeld[];
}

/** Je Wort der Eingabe die Schreibweisen, von denen eine passen muss. */
export type Suchbegriffe = string[][];

export interface Suchergebnis {
    treffer: boolean;
    /** Felder mit Treffer, die NICHT in der Zeile stehen — die Zeile nennt sie darunter. */
    fundstellen: Suchfeld[];
}

/** Text gefaltet plus, je gefaltetem Zeichen, seine Stelle im Original. */
export const falteMitKarte = (text: string): { gefaltet: string; karte: number[] } => {
    let gefaltet = '';
    const karte: number[] = [];
    for (let i = 0; i < text.length; i++) {
        const zeichen = text[i];
        const teil = zeichen === 'ß' || zeichen === 'ẞ'
            ? 'ss'
            : zeichen.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        for (const z of teil) {
            gefaltet += z;
            karte.push(i);
        }
    }
    return { gefaltet, karte };
};

export const falte = (text: string): string => falteMitKarte(text).gefaltet;

const einzeilig = (wert: unknown): string =>
    typeof wert === 'string' ? wert.replace(/\s+/g, ' ').trim() : '';

/**
 * Zerlegt die Eingabe in Woerter. Ein Punkt am Wortende faellt weg („Str."),
 * und ein Wort mit „strasse" darf auch als „str" passen — so findet
 * „Hauptstraße" die „Hauptstr. 5".
 */
export const suchbegriffe = (eingabe: string): Suchbegriffe =>
    falte(eingabe)
        .split(/\s+/)
        .map(wort => wort.replace(/\.+$/, ''))
        .filter(Boolean)
        .map(wort => (wort.includes('strasse') ? [wort, wort.replace('strasse', 'str')] : [wort]));

const beteiligterText = (kontakt: Contact): string => {
    const rolle = einzeilig(kontakt.role);
    const gewerk = einzeilig(kontakt.tradeType);
    return [
        rolle || gewerk,
        einzeilig(kontakt.name),
        einzeilig(kontakt.company),
        rolle && gewerk && falte(gewerk) !== falte(rolle) ? gewerk : '',
    ].filter(Boolean).join(' · ');
};

/** Die angezeigte Projektnummer — dieselbe Wahl wie in der Zeile. */
export const angezeigteProjektnummer = (projekt: SuchbaresProjekt | null | undefined): string =>
    einzeilig(projekt?.project_number) || einzeilig(projekt?.data?.metadata?.projectNumber);

/** Die angezeigte Adresse — Blob zuerst, sonst die Spalte. */
export const angezeigteAdresse = (projekt: SuchbaresProjekt | null | undefined): string =>
    einzeilig(projekt?.data?.metadata?.projectAddress) || einzeilig(projekt?.address);

/**
 * Alle durchsuchbaren Felder eines Listenprojekts, sichtbare zuerst.
 *
 * Doppelte Werte (Spalte und Blob sagen dasselbe, der Hauptverantwortliche
 * steht auch als Koordinator im Bericht) erscheinen nur einmal — und zwar in
 * der sichtbaren Fassung, damit die Zeile keinen Treffer doppelt nennt.
 */
export const projektSuchfelder = (projekt: SuchbaresProjekt | null | undefined, verantwortlich: string): Suchfeld[] => {
    const meta: Partial<ReportMetadata> = projekt?.data?.metadata ?? {};
    const felder: Suchfeld[] = [];
    const gesehen = new Set<string>();
    const dazu = (feld: string, wert: unknown, sichtbar = false) => {
        const text = einzeilig(wert);
        if (!text) return;
        const schluessel = falte(text);
        if (gesehen.has(schluessel)) return;
        gesehen.add(schluessel);
        felder.push({ feld, wert: text, sichtbar });
    };

    dazu('Projektname', projekt?.name, true);
    dazu('Projekt-Nr.', angezeigteProjektnummer(projekt), true);
    dazu('Adresse', angezeigteAdresse(projekt), true);
    dazu('Verantwortlich', verantwortlich, true);

    dazu('Projektname', meta.projectName);
    dazu('Projekt-Nr.', meta.projectNumber);
    dazu('Adresse', projekt?.address);
    for (const vertreter of getRepresentatives(meta as ReportMetadata)) dazu('Verantwortlich', vertreter.name);
    dazu('Im Bericht', meta.coordinator);

    dazu('Bauherr', meta.clientName);
    dazu('Bauherr', projekt?.client_name);
    dazu('Bauherr', meta.clientContactPerson);
    dazu('Bauherr', meta.clientAddress);
    dazu('Auftraggeber', meta.customerName);
    dazu('Auftraggeber', meta.customerContactPerson);
    dazu('Auftraggeber', meta.customerAddress);
    dazu('Baustelle', meta.siteContactCompany);
    dazu('Baustelle', meta.siteContactPerson);
    dazu('Baustelle', meta.siteContactAddress);

    const verteiler = Array.isArray(meta.distributionList) ? meta.distributionList : [];
    for (const kontakt of verteiler) {
        if (kontakt) dazu('Beteiligte', beteiligterText(kontakt));
    }

    dazu('Branche', meta.sifa?.branche);
    dazu('Betriebsarzt', meta.sifa?.betriebsarzt);
    dazu('Sicherheitsbeauftragte', meta.sifa?.sicherheitsbeauftragte);

    return felder;
};

export const baueSuchindex = (projekt: SuchbaresProjekt | null | undefined, verantwortlich: string): ProjektSuchindex => ({
    id: String(projekt?.id ?? ''),
    felder: projektSuchfelder(projekt, verantwortlich).map(f => ({ ...f, gefaltet: falte(f.wert) })),
});

const passt = (gefaltet: string, schreibweisen: string[]): boolean =>
    schreibweisen.some(s => gefaltet.includes(s));

/**
 * Trifft die Eingabe das Projekt?
 *
 * `eingabe` wird nur fuer den UUID-Vergleich gebraucht; alles andere laeuft
 * ueber die schon zerlegten `begriffe`.
 */
export const sucheImProjekt = (index: ProjektSuchindex, begriffe: Suchbegriffe, eingabe: string): Suchergebnis => {
    if (begriffe.length === 0) return { treffer: true, fundstellen: [] };
    const roh = eingabe.trim().toLowerCase();
    if (roh && roh === index.id.toLowerCase()) {
        return { treffer: true, fundstellen: [{ feld: 'UUID', wert: index.id, sichtbar: false }] };
    }
    const treffer = begriffe.every(schreibweisen => index.felder.some(f => passt(f.gefaltet, schreibweisen)));
    if (!treffer) return { treffer: false, fundstellen: [] };
    const fundstellen = index.felder
        .filter(f => !f.sichtbar && begriffe.some(schreibweisen => passt(f.gefaltet, schreibweisen)))
        .map(({ feld, wert, sichtbar }) => ({ feld, wert, sichtbar }));
    return { treffer: true, fundstellen };
};

/**
 * Die Stellen im ORIGINALTEXT, die markiert werden — als [von, bis)-Paare,
 * sortiert und zusammengelegt.
 */
export const trefferBereiche = (text: string, begriffe: Suchbegriffe): Array<[number, number]> => {
    if (!text || begriffe.length === 0) return [];
    const { gefaltet, karte } = falteMitKarte(text);
    const bereiche: Array<[number, number]> = [];
    for (const schreibweisen of begriffe) {
        for (const s of schreibweisen) {
            if (!s) continue;
            let stelle = gefaltet.indexOf(s);
            while (stelle !== -1) {
                let bis = karte[stelle + s.length - 1] + 1;
                // Nachgestellte Akzentzeichen (zerlegte Schreibweise „e" +
                // „´") gehoeren zum markierten Buchstaben, sonst teilt die
                // Markierung ihn in zwei Haelften.
                while (bis < text.length && /[̀-ͯ]/.test(text[bis])) bis++;
                bereiche.push([karte[stelle], bis]);
                stelle = gefaltet.indexOf(s, stelle + 1);
            }
        }
    }
    bereiche.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const zusammen: Array<[number, number]> = [];
    for (const [von, bis] of bereiche) {
        const letzter = zusammen[zusammen.length - 1];
        if (letzter && von <= letzter[1]) letzter[1] = Math.max(letzter[1], bis);
        else zusammen.push([von, bis]);
    }
    return zusammen;
};
